/**
 * useRewardedAds Hook
 * Manages Google AdMob Rewarded Video Ads for earning credits
 * 
 * Features:
 * - Load and show rewarded ads
 * - Track daily limits
 * - Handle rewards server-side
 * - Country-based tracking
 * 
 * NOTE: AdMob package temporarily removed for build compatibility.
 * Will be re-added when AAB build is stable.
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// AdMob temporarily disabled - will be enabled when package is re-added
const AdMobAvailable = false;

// Check if running on native platform
const isNativePlatform = () => {
  if (typeof window !== 'undefined' && window.Capacitor) {
    return window.Capacitor.isNativePlatform();
  }
  return false;
};

export const useRewardedAds = (userId) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAdReady, setIsAdReady] = useState(false);
  const [error, setError] = useState(null);
  const [adConfig, setAdConfig] = useState(null);
  const [rewardStatus, setRewardStatus] = useState({
    enabled: false,
    rewards_remaining: 0,
    can_watch_ad: false,
    credits_per_ad: 1
  });
  
  // Get user's country from browser/device
  const getUserCountry = () => {
    try {
      // Try to get from navigator.language
      const lang = navigator.language || navigator.userLanguage || 'en-US';
      const parts = lang.split('-');
      if (parts.length > 1) {
        return parts[1].toUpperCase();
      }
      // Default to TR for Turkish users
      if (lang.toLowerCase().startsWith('tr')) {
        return 'TR';
      }
      // Default to AZ for Azerbaijani users  
      if (lang.toLowerCase().startsWith('az')) {
        return 'AZ';
      }
      return 'unknown';
    } catch (e) {
      return 'unknown';
    }
  };

  // Initialize AdMob (temporarily disabled)
  const initializeAdMob = useCallback(async () => {
    if (!AdMobAvailable || !isNativePlatform()) {
      console.log('📱 AdMob: Temporarily disabled or not on native platform');
      return false;
    }
    // AdMob initialization will be added back when package is re-installed
    return false;
  }, []);

  // Fetch ad configuration from backend
  const fetchAdConfig = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/ads/config`);
      setAdConfig(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch ad config:', err);
      return null;
    }
  }, []);

  // Fetch user's reward status
  const fetchRewardStatus = useCallback(async () => {
    if (!userId) return;
    
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API_URL}/api/ads/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRewardStatus(data);
      return data;
    } catch (err) {
      console.error('Failed to fetch reward status:', err);
      return null;
    }
  }, [userId]);

  // Load rewarded ad (temporarily disabled)
  const loadRewardedAd = useCallback(async () => {
    if (!AdMobAvailable || !isNativePlatform() || !adConfig?.ad_unit_id) {
      return false;
    }
    // AdMob ad loading will be added back when package is re-installed
    return false;
  }, [adConfig]);

  // Show rewarded ad and claim reward (temporarily disabled - simulation mode)
  const showRewardedAd = useCallback(async () => {
    if (!AdMobAvailable) {
      // For now, return a message that AdMob is coming soon
      setError('Reklam özelliği yakında aktif olacak');
      return { success: false, error: 'AdMob coming soon' };
    }
    
    if (!isNativePlatform()) {
      setError('Bu özellik sadece mobil uygulamada kullanılabilir');
      return { success: false, error: 'Not on native platform' };
    }

    if (!rewardStatus.can_watch_ad) {
      setError('Günlük reklam limitinize ulaştınız');
      return { success: false, error: 'Daily limit reached' };
    }

    // AdMob ad showing will be added back when package is re-installed
    return { success: false, error: 'AdMob temporarily disabled' };
  }, [rewardStatus]);

  // Initialize on mount
  useEffect(() => {
    const init = async () => {
      const config = await fetchAdConfig();
      if (config?.enabled) {
        await fetchRewardStatus();
        if (AdMobAvailable && isNativePlatform()) {
          await initializeAdMob();
          await loadRewardedAd();
        }
      } else {
        await fetchRewardStatus();
      }
    };

    if (userId) {
      init();
    }
  }, [userId, fetchAdConfig, initializeAdMob, fetchRewardStatus, loadRewardedAd]);

  return {
    // State
    isLoading,
    isAdReady,
    error,
    rewardStatus,
    adConfig,
    isNative: isNativePlatform(),
    
    // Actions
    showRewardedAd,
    loadRewardedAd,
    refreshStatus: fetchRewardStatus,
    clearError: () => setError(null)
  };
};

export default useRewardedAds;
